import React, { useState, useEffect, useCallback } from 'react';
import { weeklyTruckReportAPI } from '../../lib/api';
import api from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { Layout } from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../components/ui/tooltip';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Loader2, RefreshCw, ShieldCheck, AlertTriangle, CircleAlert,
  Truck, Package, TrendingUp, BarChart3, CalendarDays, Info,
  ChevronLeft, ChevronRight, GitCompareArrows, Download, FileDown,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, Legend,
  ReferenceLine,
} from 'recharts';

function InfoTip({ text }) {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Info className="w-3.5 h-3.5 text-muted-foreground cursor-help inline ml-1" />
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs text-xs">
          <p>{text}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function StatCard({ title, value, subtitle, icon: Icon, color, tip, testId }) {
  return (
    <Card className="relative overflow-hidden" data-testid={testId}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div className="space-y-1">
            <p className="text-sm text-muted-foreground flex items-center">
              {title}
              {tip && <InfoTip text={tip} />}
            </p>
            <p className={`text-3xl font-bold tracking-tight ${color || 'text-foreground'}`}>
              {value}
            </p>
            {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
          </div>
          {Icon && (
            <div className={`p-2 rounded-lg ${color ? 'bg-muted/50' : 'bg-muted/50'}`}>
              <Icon className={`w-5 h-5 ${color || 'text-muted-foreground'}`} />
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default function WeeklyTruckReport() {
  const { isOwner } = useAuth();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [weekOffset, setWeekOffset] = useState(0);
  const [compareEnabled, setCompareEnabled] = useState(false);
  const [prevWeekData, setPrevWeekData] = useState(null);
  const [readinessGoal, setReadinessGoal] = useState(90);

  // Fetch readiness goal from operations settings
  useEffect(() => {
    api.get('/company-settings/operations')
      .then(res => setReadinessGoal(res.data.readiness_goal_pct ?? 90))
      .catch(() => {});
  }, []);

  const fetchReport = useCallback(async (offset = 0) => {
    setLoading(true);
    try {
      const params = {};
      if (offset !== 0) {
        const end = new Date();
        end.setDate(end.getDate() + offset * 7);
        const start = new Date(end);
        start.setDate(start.getDate() - 6);
        params.start_date = start.toISOString().split('T')[0];
        params.end_date = end.toISOString().split('T')[0];
      }
      const res = await weeklyTruckReportAPI.getWeekly(params);
      setReport(res.data);
    } catch (err) {
      toast.error('Failed to load weekly report');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch previous week when compare is toggled on or week changes
  useEffect(() => {
    if (!compareEnabled || !report) { setPrevWeekData(null); return; }
    const fetchPrev = async () => {
      try {
        const end = new Date(report.start_date + 'T12:00:00');
        end.setDate(end.getDate() - 1);
        const start = new Date(end);
        start.setDate(start.getDate() - 6);
        const res = await weeklyTruckReportAPI.getWeekly({
          start_date: start.toISOString().split('T')[0],
          end_date: end.toISOString().split('T')[0],
        });
        setPrevWeekData(res.data);
      } catch { setPrevWeekData(null); }
    };
    fetchPrev();
  }, [compareEnabled, report]);

  useEffect(() => {
    fetchReport(weekOffset);
  }, [weekOffset, fetchReport]);

  const handlePrevWeek = () => setWeekOffset(prev => prev - 1);
  const handleNextWeek = () => { if (weekOffset < 0) setWeekOffset(prev => prev + 1); };

  const formatDateRange = () => {
    if (!report) return '';
    const s = new Date(report.start_date + 'T12:00:00');
    const e = new Date(report.end_date + 'T12:00:00');
    const fmt = (d) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    return `${fmt(s)} - ${fmt(e)}`;
  };

  const rateColor = (rate) => {
    if (rate >= 80) return 'text-green-600 dark:text-green-400';
    if (rate >= 50) return 'text-amber-600 dark:text-amber-400';
    return 'text-red-600 dark:text-red-400';
  };

  const goalColor = (rate) => {
    if (rate >= readinessGoal) return 'text-green-600 dark:text-green-400';
    if (rate >= readinessGoal - 5) return 'text-amber-600 dark:text-amber-400';
    return 'text-red-600 dark:text-red-400';
  };

  const goalLabel = (rate) => {
    if (rate >= readinessGoal) return { text: 'At Goal', cls: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' };
    if (rate >= readinessGoal - 5) return { text: 'Near Goal', cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' };
    return { text: 'Below Goal', cls: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' };
  };

  const exportCsv = () => {
    if (!report) return;
    const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const lines = [];

    // Summary
    lines.push('WEEKLY TRUCK PREP SUMMARY');
    lines.push(`Period,${report.start_date} to ${report.end_date}`);
    lines.push(`Timezone,${report.timezone || ''}`);
    lines.push('');
    lines.push('Metric,Value');
    lines.push(`Readiness Rate,${report.readiness_rate}%`);
    lines.push(`Total Jobs,${report.total_jobs}`);
    lines.push(`Ready,${report.ready_count}`);
    lines.push(`Missing Parts,${report.missing_count}`);
    lines.push(`Low Stock,${report.low_stock_count}`);
    lines.push(`No Vehicle,${report.no_vehicle_count || 0}`);
    lines.push(`No Kit,${report.no_kit_count || 0}`);
    lines.push(`Readiness Goal,${readinessGoal}%`);

    // Daily Breakdown
    lines.push('');
    lines.push('DAILY BREAKDOWN');
    lines.push('Date,Total Jobs,Ready,Missing,Day Rate');
    (report.daily_stats || []).forEach(d => {
      const dayRate = d.total > 0 ? Math.round((d.ready / d.total) * 100) : 100;
      lines.push(`${d.date},${d.total},${d.ready},${d.missing},${dayRate}%`);
    });

    // Missing Parts
    lines.push('');
    lines.push('MOST FREQUENTLY MISSING PARTS');
    lines.push('Part Name,Times Missing');
    (report.top_missing_parts || []).forEach(p => {
      lines.push(`${esc(p.item_name)},${p.frequency}`);
    });

    // Vehicles Needing Restock
    lines.push('');
    lines.push('VEHICLES NEEDING MOST RESTOCKING');
    lines.push('Vehicle,Restock Events');
    (report.top_vehicles_needing_restock || []).forEach(v => {
      lines.push(`${esc(v.vehicle_name)},${v.restock_count}`);
    });

    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `truck-prep-report_${report.start_date}_${report.end_date}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('CSV exported');
  };

  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const exportPdf = async () => {
    if (!report) return;
    setDownloadingPdf(true);
    try {
      const params = { start_date: report.start_date, end_date: report.end_date };
      const res = await weeklyTruckReportAPI.downloadPdf(params);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `truck-prep-report_${report.start_date}_${report.end_date}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch (err) {
      toast.error('Failed to download PDF');
      console.error(err);
    } finally {
      setDownloadingPdf(false);
    }
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="weekly-truck-report-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold tracking-tight" data-testid="weekly-report-title">
              Weekly Truck Prep Summary
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Truck readiness performance over the last 7 days
              <InfoTip text="Shows aggregated readiness data for all scheduled jobs. Updated based on your tenant's configured timezone." />
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="icon" onClick={handlePrevWeek} data-testid="prev-week-btn">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm font-medium min-w-[200px] text-center" data-testid="date-range-label">
              {loading ? '...' : formatDateRange()}
            </span>
            <Button variant="outline" size="icon" onClick={handleNextWeek} disabled={weekOffset >= 0} data-testid="next-week-btn">
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={() => fetchReport(weekOffset)} disabled={loading} data-testid="refresh-report-btn">
              <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
            <Button variant="outline" size="sm" onClick={exportCsv} disabled={loading || !report} data-testid="export-csv-btn">
              <Download className="h-4 w-4 mr-1" /> Export CSV
            </Button>
            <Button variant="outline" size="sm" onClick={exportPdf} disabled={loading || !report || downloadingPdf} data-testid="export-pdf-btn">
              {downloadingPdf ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <FileDown className="h-4 w-4 mr-1" />}
              Download PDF
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : !report ? (
          <Card><CardContent className="py-12 text-center text-muted-foreground">No report data available.</CardContent></Card>
        ) : (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4" data-testid="summary-cards">
              <Card className="relative overflow-hidden" data-testid="card-readiness-rate">
                <CardContent className="p-5">
                  <div className="flex items-start justify-between">
                    <div className="space-y-1">
                      <p className="text-sm text-muted-foreground flex items-center">
                        Readiness Rate
                        <InfoTip text={`Shows whether your weekly readiness rate meets your company's ${readinessGoal}% goal. Green = at or above goal, Amber = within 5 points, Red = more than 5 points below.`} />
                      </p>
                      <p className={`text-3xl font-bold tracking-tight ${goalColor(report.readiness_rate)}`}>
                        {report.readiness_rate}%
                      </p>
                      <span className={`inline-block text-[11px] font-medium px-1.5 py-0.5 rounded ${goalLabel(report.readiness_rate).cls}`} data-testid="readiness-goal-badge">
                        {goalLabel(report.readiness_rate).text}
                      </span>
                    </div>
                    <div className="p-2 rounded-lg bg-muted/50">
                      <TrendingUp className={`w-5 h-5 ${goalColor(report.readiness_rate)}`} />
                    </div>
                  </div>
                </CardContent>
              </Card>
              <StatCard
                title="Total Jobs"
                value={report.total_jobs}
                icon={CalendarDays}
                tip="Total scheduled jobs found in this period."
                testId="card-total-jobs"
              />
              <StatCard
                title="Ready"
                value={report.ready_count}
                icon={ShieldCheck}
                color="text-green-600 dark:text-green-400"
                tip="Jobs where the vehicle had all required parts in sufficient quantity."
                testId="card-ready"
              />
              <StatCard
                title="Missing Parts"
                value={report.missing_count}
                icon={CircleAlert}
                color="text-red-600 dark:text-red-400"
                tip="Jobs where the vehicle was missing one or more required parts."
                testId="card-missing"
              />
              <StatCard
                title="Low Stock"
                value={report.low_stock_count}
                icon={AlertTriangle}
                color="text-amber-600 dark:text-amber-400"
                tip="Jobs where parts were available but would drop below minimum stock levels."
                testId="card-low-stock"
              />
              <StatCard
                title="No Vehicle / Kit"
                value={(report.no_vehicle_count || 0) + (report.no_kit_count || 0)}
                icon={Truck}
                color="text-muted-foreground"
                tip="Jobs that had no vehicle assigned or no job kit configured."
                testId="card-no-vehicle"
              />
            </div>

            {/* Charts */}
            {report.daily_stats?.length > 0 && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="charts-section">
                {/* Readiness Trend Line Chart */}
                <Card data-testid="readiness-trend-chart">
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base flex items-center gap-2">
                        <TrendingUp className="w-4 h-4 text-primary" />
                        Readiness Trend
                        <InfoTip text={`Daily readiness rate (%) over the selected period. The dashed blue line marks your ${readinessGoal}% goal (configurable in Operations settings). Higher is better.`} />
                      </CardTitle>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant={compareEnabled ? 'default' : 'outline'}
                              size="sm"
                              className="h-7 text-xs gap-1.5"
                              onClick={() => setCompareEnabled(p => !p)}
                              data-testid="compare-toggle-btn"
                            >
                              <GitCompareArrows className="w-3.5 h-3.5" />
                              {compareEnabled ? 'Comparing' : 'Compare'}
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent side="left" className="text-xs max-w-[200px]">
                            <p>Toggle to overlay the previous week's readiness trend for comparison.</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="h-[220px] w-full">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={report.daily_stats.map((d, i) => {
                          const dt = new Date(d.date + 'T12:00:00');
                          const row = {
                            day: dt.toLocaleDateString('en-US', { weekday: 'short' }),
                            rate: d.total > 0 ? Math.round((d.ready / d.total) * 100) : 100,
                          };
                          if (compareEnabled && prevWeekData?.daily_stats?.[i]) {
                            const p = prevWeekData.daily_stats[i];
                            row.prevRate = p.total > 0 ? Math.round((p.ready / p.total) * 100) : 100;
                          }
                          return row;
                        })} margin={{ top: 8, right: 12, left: -12, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                          <XAxis dataKey="day" tick={{ fontSize: 12 }} className="text-muted-foreground" />
                          <YAxis domain={[0, 100]} tick={{ fontSize: 12 }} tickFormatter={v => `${v}%`} className="text-muted-foreground" />
                          <ReferenceLine
                            y={readinessGoal} stroke="hsl(220, 70%, 55%)" strokeDasharray="4 4" strokeWidth={1.5}
                            label={{ value: `${readinessGoal}% Goal`, position: 'insideTopRight', fill: 'hsl(220, 70%, 55%)', fontSize: 11, fontWeight: 500 }}
                          />
                          <RTooltip
                            contentStyle={{ borderRadius: 8, fontSize: 13, border: '1px solid hsl(var(--border))' }}
                            formatter={(v, name) => [`${v}%`, name]}
                          />
                          {compareEnabled && (
                            <Legend wrapperStyle={{ fontSize: 12 }} />
                          )}
                          {compareEnabled && prevWeekData && (
                            <Line
                              type="monotone" dataKey="prevRate" name="Previous Week"
                              stroke="hsl(220, 9%, 60%)" strokeWidth={2}
                              strokeDasharray="6 3"
                              dot={{ r: 3, fill: 'hsl(220, 9%, 60%)' }}
                              activeDot={{ r: 5 }}
                            />
                          )}
                          <Line
                            type="monotone" dataKey="rate" name={compareEnabled ? 'This Week' : 'Readiness %'}
                            stroke="hsl(142, 71%, 45%)" strokeWidth={2.5}
                            dot={{ r: 4, fill: 'hsl(142, 71%, 45%)' }}
                            activeDot={{ r: 6 }}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>

                {/* Missing / Not Ready Bar Chart */}
                <Card data-testid="missing-bar-chart">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <BarChart3 className="w-4 h-4 text-red-500" />
                      Missing Parts &amp; Not-Ready Jobs
                      <InfoTip text="Number of jobs with missing parts each day. Lower is better." />
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-[220px] w-full">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={report.daily_stats.map(d => {
                          const dt = new Date(d.date + 'T12:00:00');
                          return {
                            day: dt.toLocaleDateString('en-US', { weekday: 'short' }),
                            missing: d.missing,
                            total: d.total,
                            notReady: d.total - d.ready,
                          };
                        })} margin={{ top: 8, right: 12, left: -12, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                          <XAxis dataKey="day" tick={{ fontSize: 12 }} className="text-muted-foreground" />
                          <YAxis allowDecimals={false} tick={{ fontSize: 12 }} className="text-muted-foreground" />
                          <RTooltip
                            contentStyle={{ borderRadius: 8, fontSize: 13, border: '1px solid hsl(var(--border))' }}
                          />
                          <Legend wrapperStyle={{ fontSize: 12 }} />
                          <Bar dataKey="missing" name="Missing Parts" fill="hsl(0, 84%, 60%)" radius={[3, 3, 0, 0]} />
                          <Bar dataKey="notReady" name="Not Ready" fill="hsl(38, 92%, 50%)" radius={[3, 3, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Most Frequently Missing Parts */}
              <Card data-testid="missing-parts-table">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Package className="w-4 h-4 text-red-500" />
                    Most Frequently Missing Parts
                    <InfoTip text="Parts that were most often unavailable across all jobs in this period." />
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {report.top_missing_parts?.length > 0 ? (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Part Name</TableHead>
                          <TableHead className="text-right w-[100px]">Times Missing</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {report.top_missing_parts.map((part, i) => (
                          <TableRow key={i} data-testid={`missing-part-row-${i}`}>
                            <TableCell className="font-medium">{part.item_name}</TableCell>
                            <TableCell className="text-right">
                              <Badge variant="destructive" className="text-xs">{part.frequency}x</Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <p className="text-sm text-muted-foreground text-center py-6" data-testid="no-missing-parts">
                      No missing parts this period. All vehicles were stocked.
                    </p>
                  )}
                </CardContent>
              </Card>

              {/* Vehicles Needing Most Restocking */}
              <Card data-testid="vehicles-restock-table">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Truck className="w-4 h-4 text-amber-500" />
                    Vehicles Needing Most Restocking
                    <InfoTip text="Vehicles that most frequently had missing parts across jobs in this period." />
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {report.top_vehicles_needing_restock?.length > 0 ? (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Vehicle</TableHead>
                          <TableHead className="text-right w-[120px]">Restock Events</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {report.top_vehicles_needing_restock.map((v, i) => (
                          <TableRow key={i} data-testid={`restock-vehicle-row-${i}`}>
                            <TableCell className="font-medium">{v.vehicle_name}</TableCell>
                            <TableCell className="text-right">
                              <Badge variant="outline" className="text-xs border-amber-400 text-amber-700 dark:text-amber-400">
                                {v.restock_count} restock{v.restock_count !== 1 ? 's' : ''}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <p className="text-sm text-muted-foreground text-center py-6" data-testid="no-restocks-needed">
                      No vehicles needed restocking this period.
                    </p>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Daily Breakdown */}
            <Card data-testid="daily-breakdown-table">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <BarChart3 className="w-4 h-4 text-primary" />
                  Daily Breakdown
                  <InfoTip text="Day-by-day summary showing total jobs, ready count, and missing parts count for each day in the period." />
                </CardTitle>
              </CardHeader>
              <CardContent>
                {report.daily_stats?.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead className="text-right">Total Jobs</TableHead>
                        <TableHead className="text-right">Ready</TableHead>
                        <TableHead className="text-right">Missing</TableHead>
                        <TableHead className="text-right">Day Rate</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {report.daily_stats.map((day, i) => {
                        const dayRate = day.total > 0
                          ? Math.round((day.ready / day.total) * 100)
                          : 100;
                        const d = new Date(day.date + 'T12:00:00');
                        const dayLabel = d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
                        return (
                          <TableRow key={i} data-testid={`daily-row-${i}`}>
                            <TableCell className="font-medium">{dayLabel}</TableCell>
                            <TableCell className="text-right">{day.total}</TableCell>
                            <TableCell className="text-right text-green-600 dark:text-green-400">{day.ready}</TableCell>
                            <TableCell className="text-right text-red-600 dark:text-red-400">{day.missing}</TableCell>
                            <TableCell className="text-right">
                              <span className={`font-medium ${rateColor(dayRate)}`}>{dayRate}%</span>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-6">No daily data available.</p>
                )}
              </CardContent>
            </Card>

            {/* Timezone Footer */}
            {report.timezone && (
              <p className="text-xs text-muted-foreground text-right" data-testid="timezone-label">
                All dates in {report.timezone}
              </p>
            )}
          </>
        )}
      </div>
    </Layout>
  );
}
